import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SettingsChange, SettingsChangeValue, SettingsHistorySection } from '@ace-tracker/shared';
import { fetchSettingsHistory, UnauthorizedError } from '../../api';
import { DeveloperName } from '../../settings';
import { Card, SelectInput } from './formParts';

export const SETTINGS_HISTORY_QUERY_KEY = ['settings-history'] as const;

const SECTION_LABELS: Record<SettingsHistorySection, string> = {
  developers: 'Developers',
  teams: 'Teams',
  models: 'Model pricing',
  cost: 'Cost & currency',
  executive: 'Executive',
  features: 'Feature flags',
  dataHealth: 'Data health',
  targets: 'Budgets & goals',
  account: 'Account',
};

const FIELD_LABELS: Record<string, string> = {
  displayName: 'Display name',
  team: 'Team',
  excluded: 'Excluded',
  provider: 'Provider',
  inputPerMillion: 'Input price per million',
  outputPerMillion: 'Output price per million',
  cacheReadPerMillion: 'Cache-read price per million',
  cacheWritePerMillion: 'Cache-write price per million',
  notes: 'Notes',
  currency: 'Currency',
  copilotCostPerAiu: 'Copilot cost per AIU',
  source: 'Cost basis',
  engineersInScope: 'Engineers in scope',
  adoptionTargetPercent: 'Adoption target (%)',
  habitualThresholdPercent: 'Habitual-user threshold (%)',
  minTeamSize: 'Minimum team size',
  copilot: 'GitHub Copilot',
  'claude-code': 'Claude Code',
  codex: 'OpenAI Codex',
  kiro: 'AWS Kiro',
  gemini: 'Gemini CLI',
  cursor: 'Cursor',
  windsurf: 'Windsurf',
  rtk: 'RTK',
  headroom: 'Headroom',
  graphify: 'Graphify',
  password: 'Password',
};

const LIMIT = 200;

function formatValue(value: SettingsChangeValue): string {
  if (value === null) return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function Subject({ section, subject }: { section: SettingsHistorySection; subject: string }) {
  if (section === 'developers') return <DeveloperName developerId={subject} />;
  if (subject === 'fallbackPricing') return <span className="text-text-default">Fallback rate</span>;
  return <span className="break-all font-mono text-xs text-text-default">{subject}</span>;
}

function ChangeLine({ section, change }: { section: SettingsHistorySection; change: SettingsChange }) {
  // A team being added or removed, and a password change, have no meaningful "before → after".
  const summary =
    section === 'teams' ? (change.after === null ? 'Removed' : 'Added')
    : section === 'account' ? 'Changed'
    : undefined;
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      {change.subject && <Subject section={section} subject={change.subject} />}
      {summary ? (
        <span className="text-text-muted">{section === 'account' ? `${FIELD_LABELS[change.field] ?? change.field} changed` : summary}</span>
      ) : (
        <span className="text-text-muted">
          {FIELD_LABELS[change.field] ?? change.field}:{' '}
          <span className="text-text-default">{formatValue(change.before)}</span>
          <span aria-hidden="true" className="px-1.5">→</span>
          <span className="sr-only"> changed to </span>
          <span className="font-medium text-text-default">{formatValue(change.after)}</span>
        </span>
      )}
    </li>
  );
}

/** Settings → History: every saved change, newest first, with the value before and after. */
export function HistorySettings() {
  const [section, setSection] = useState<SettingsHistorySection | ''>('');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [...SETTINGS_HISTORY_QUERY_KEY, section],
    queryFn: () => fetchSettingsHistory({ section: section || undefined, limit: LIMIT }),
    staleTime: 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 1,
  });

  return (
    <Card
      title="Change history"
      description="Every saved change to these settings, newest first, with who made it and the value before and after. Records are only ever added, never edited or removed."
    >
      <div className="max-w-xs">
        <label htmlFor="history-section" className="block text-sm font-medium text-text-default">Section</label>
        <SelectInput
          id="history-section"
          className="mt-1.5"
          value={section}
          onChange={(event) => setSection(event.target.value as SettingsHistorySection | '')}
        >
          <option value="">All sections</option>
          {(Object.keys(SECTION_LABELS) as SettingsHistorySection[]).map((id) => (
            <option key={id} value={id}>{SECTION_LABELS[id]}</option>
          ))}
        </SelectInput>
      </div>

      <div className="mt-5" aria-live="polite">
        {isLoading && <p className="text-sm font-light text-text-muted">Loading history…</p>}
        {isError && (
          <p className="text-sm font-medium text-error">
            <i aria-hidden="true" className="fa-solid fa-circle-exclamation mr-1.5" />
            Could not load the change history: {(error as Error).message}
          </p>
        )}
        {data && data.records.length === 0 && (
          <p className="text-sm text-text-muted">
            No changes recorded yet{section ? ' for this section' : ''}. History starts with the first change saved after this feature was installed.
          </p>
        )}
      </div>

      {data && data.records.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                <tr>
                  <th scope="col" className="w-[11rem] pb-2.5 pr-3">When</th>
                  <th scope="col" className="w-[8rem] pb-2.5 pr-3">Changed by</th>
                  <th scope="col" className="w-[9rem] pb-2.5 pr-3">Section</th>
                  <th scope="col" className="pb-2.5">What changed</th>
                </tr>
              </thead>
              <tbody>
                {data.records.map((record) => (
                  <tr key={record.id} className="border-b border-border align-top last:border-0">
                    <td className="py-2.5 pr-3 tabular-nums text-text-muted">{new Date(record.at).toLocaleString()}</td>
                    <td className="break-all py-2.5 pr-3 text-text-default">{record.by}</td>
                    <td className="py-2.5 pr-3 text-text-default">{SECTION_LABELS[record.section]}</td>
                    <td className="py-2.5">
                      <ul className="space-y-1.5">
                        {record.changes.map((change, i) => (
                          <ChangeLine key={i} section={record.section} change={change} />
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.total > data.records.length && (
            <p className="mt-3 text-xs text-text-muted">
              Showing the {data.records.length} most recent of {data.total} records.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
